"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const STORAGE_KEY = "peakwave:jobs-overview:collapsed";

export function JobsOverview({ children }: { children: ReactNode }) {
  const contentId = useId();
  const [collapsed, setCollapsed] = useState(false);
  const interacted = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (interacted.current) return;
      try {
        setCollapsed(window.localStorage.getItem(STORAGE_KEY) === "true");
      } catch {
        // The overview remains usable when browser storage is blocked.
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  function toggle() {
    const next = !collapsed;
    interacted.current = true;
    setCollapsed(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // Keep the current visit's choice even if it cannot be remembered.
    }
  }

  return (
    <section aria-label="Your jobs overview" className="border-b border-border-light bg-warm-bg px-5 py-3 md:px-9 md:py-4">
      <div className="mx-auto max-w-[1160px]">
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-heading text-2xl font-bold text-navy">Your jobs</h1>
          <button
            type="button"
            aria-expanded={!collapsed}
            aria-controls={contentId}
            onClick={toggle}
            className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-sm px-2 text-[13px] font-semibold text-teal-dark hover:bg-teal/5 focus-visible:outline-2 focus-visible:outline-teal"
          >
            {collapsed ? "Show overview" : "Hide overview"}
            {collapsed ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronUp size={16} aria-hidden="true" />}
          </button>
        </div>
        <div id={contentId} hidden={collapsed} className="mt-2">
          {children}
        </div>
      </div>
    </section>
  );
}
